import React from "react";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { PaymentGatewayType } from "shared/utils/enums";
import { useGetPaymentSdkToken } from "./dataloader";
import { BraintreePayment } from "./model/braintree";
import { PaymentInterface } from "./model/paymentInterface";
import Reducer from "./reducer";
import { PaymentDispatchInterface, PaymentService, PaymentServiceProps, PaymentState } from "./types";
import { StripePayment } from "./model/stripe";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import { RedirectQueryParam } from "shared/enums/Redirect";

export default function usePaymentService({
  amountObj,
  setPaymentNonce,
  registrationParticipantId,
  purchaseOrderId,
  stripeReturnUrl,
  invoicePayerId,
  onTokenInitialised,
  errorHandler
}: PaymentServiceProps): PaymentService {
  const [state, dispatch] = useReducerWrapper<PaymentState, PaymentDispatchInterface>(Reducer);
  const paymentInterfaceRef = React.useRef<PaymentInterface>();
  const { mutate: getInitToken, data: apiPaymentInitToken } = useGetPaymentSdkToken(
    registrationParticipantId,
    purchaseOrderId,
    invoicePayerId,
    onTokenInitialised,
    errorHandler
  );
  const [stripePromise, setStripePromise] = React.useState<Promise<Stripe | null>>();
  const { navigate } = useNavigateWrapper();

  React.useEffect(() => {
    if (registrationParticipantId || invoicePayerId) {
      // intentionally passing empty object so that POST is called
      getInitToken({});
    }
  }, [getInitToken, registrationParticipantId, invoicePayerId]);

  const setPayButtonDisabled = React.useCallback(
    isButtonDisabled => {
      dispatch.setIsPayButtonDisabled(isButtonDisabled);
    },
    [dispatch]
  );

  const setPaymentError = React.useCallback(
    errorMessage => {
      dispatch.setPaymentError(errorMessage);
    },
    [dispatch]
  );

  const setDisplayLoader = React.useCallback(
    showLoader => {
      dispatch.setDisplayLoader(showLoader);
    },
    [dispatch]
  );

  React.useEffect(() => {
    // if payment interface is not yet initialized then we need to initialize it based on payment gateway type
    if (!paymentInterfaceRef.current) {
      if (apiPaymentInitToken?.paymentGatewayType === PaymentGatewayType.BRAINTREE) {
        // create instance of Braintree
        paymentInterfaceRef.current = new BraintreePayment({
          apiPaymentInitToken,
          setPayButtonDisabled,
          setPaymentError,
          amountObj,
          setDisplayLoader,
          setPaymentNonce
        });
      } else if (apiPaymentInitToken?.paymentGatewayType === PaymentGatewayType.STRIPE) {
        if (apiPaymentInitToken.paymentIntentId) {
          // getting paymentIntentId in initToken response denotes that a payment was already made for this
          //    but FE did not call the Pay API (e.g. after payment tab/browser was closed, etc.)
          // In such cases we will proceed to make the API call and do further processing as per flow
          if (stripeReturnUrl) {
            // the redirect flow is currently used in Registration portal
            // break the stripe return URL into pathname and search params so it can be used with navigate
            const { pathname, search } = new URL(stripeReturnUrl);
            navigate(
              {
                pathname,
                search: `${search}&${RedirectQueryParam.STRIPE_PAYMENT_INTENT_ID}=${
                  apiPaymentInitToken.paymentIntentId
                }&${RedirectQueryParam.AMOUNT_OBJECT}=${JSON.stringify(amountObj)}`
              },
              { replace: true }
            );
          } else {
            // handling for non-redirect flow for console and myExp
            setPaymentNonce(apiPaymentInitToken.paymentIntentId);
          }
          // we do not want to load stripe component since payment was already made
          return;
        }
        // create instance of Stripe
        // although we have this interface here, we are not using StripePayment class for now (initialization stage)
        paymentInterfaceRef.current = new StripePayment({
          apiPaymentInitToken,
          setPayButtonDisabled,
          setPaymentError,
          amountObj,
          setDisplayLoader,
          setPaymentNonce
        });
        setStripePromise(loadStripe(apiPaymentInitToken.publicKey as string));
      }
      // initialize the instance created
      paymentInterfaceRef.current?.initialize();
    } else if (apiPaymentInitToken) {
      paymentInterfaceRef.current?.updateInitToken?.(apiPaymentInitToken?.initToken);
      paymentInterfaceRef.current?.retryGetTokenForPayment();
    }
  }, [
    amountObj,
    apiPaymentInitToken,
    setDisplayLoader,
    setPaymentNonce,
    setPayButtonDisabled,
    setPaymentError,
    navigate,
    stripeReturnUrl
  ]);

  const getTokenForPayment = React.useCallback(() => {
    paymentInterfaceRef.current?.getTokenForPayment();
  }, []);

  const retryGetTokenForPayment = React.useCallback(() => paymentInterfaceRef.current?.retryGetTokenForPayment(), []);

  React.useEffect(() => {
    if (state.paymentError) {
      // todo:rr display error
    }
  }, [state.paymentError]);

  const paymentGatewayType = React.useMemo(
    () => apiPaymentInitToken?.paymentGatewayType,
    [apiPaymentInitToken?.paymentGatewayType]
  );

  // todo:rr clean up later on
  const getNewInitToken = React.useCallback(() => {
    getInitToken({});
  }, [getInitToken]);

  return {
    getTokenForPayment,
    retryGetTokenForPayment,
    isPayButtonDisabled: state.isPayButtonDisabled,
    displayLoader: state.displayLoader,
    paymentGatewayType,
    stripePromise,
    apiPaymentInitToken,
    setDisplayLoader,
    setPayButtonDisabled,
    getNewInitToken
  };
}
